import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
} from '@mui/material';
import { AppButton, AppTextField } from '../../../components/design-system';
import { HowToReg as ClaimIcon } from '@mui/icons-material';
import { providerOrdersService } from '../../../services/ProviderOrdersService';

interface ProviderClaimOnBehalfModalProps {
  open: boolean;
  orderReference: string;
  tenantName?: string | null;
  onClose: () => void;
  onSuccess: (devicesClaimed: number) => void;
}

export default function ProviderClaimOnBehalfModal({
  open,
  orderReference,
  tenantName,
  onClose,
  onSuccess,
}: ProviderClaimOnBehalfModalProps) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setReason('');
    setConfirmed(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    if (!confirmed) {
      setError('Please confirm the action by checking the box.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await providerOrdersService.claimOrderOnBehalf(orderReference, { reason: reason.trim() });
      onSuccess(res.data.devicesClaimed);
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Claim failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ClaimIcon color="primary" />
        Claim on Behalf of Tenant
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Order:</strong> {orderReference}
          </Typography>
          {tenantName && (
            <Typography variant="body2" color="text.secondary">
              <strong>Tenant:</strong> {tenantName}
            </Typography>
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Use for white-glove delivery when Cnapse installs on site and the tenant cannot enter the
          shipment claim code. This performs the same transitions as tenant self-service claim.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <AppTextField
          label="Reason for provider claim *"
          fullWidth
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., On-site install with customer facilities manager present"
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          }
          label="I confirm the tenant has received this shipment and devices should move to claimed/active."
        />
      </DialogContent>
      <DialogActions>
        <AppButton onClick={handleClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !confirmed || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Claim for Tenant
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
